import React, { useMemo, useCallback, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
	Typography,
	Select,
	Button,
	MenuItem,
	Box,
	Checkbox,
	ListItemText,
	IconButton,
	SelectChangeEvent,
	FormControlLabel,
	Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { getRentleSegments } from 'common/utils/segments';
import ContainerBox from 'components/ContainerBox';
import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as ActiveProductActions from 'actions/ActiveProductActions';
import * as UserSelectors from 'selectors/UserSelectors';

import useDebouncedForm from 'common/hooks/useDebouncedForm';
import { getDefaultExtensionPricing } from 'common/utils/extensions';

import PricingTable from 'views/Product/__components/PricingTable';
import ExtensionPricing from 'views/Product/__components/ExtensionPricing';
import HasFeature from 'components/features/HasFeature';
import { useTranslation } from 'services/localization/useTranslation';
import AddOnPromoBlock from 'components/features/promos/AddOnPromoBlock';
import VariantRentalPriceTable from './components/VariantRentalPriceTable';
import { ProductVariant } from 'common/modules/inventory';
import { isAnyVariantForRent } from 'common/modules/products/utils';
import Banner from 'components/Banner';

const SEGMENTS = getRentleSegments();

const BookingPricing = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const variantTableRef = useRef<HTMLDivElement>(null);
	const product = useSelector(ActiveProductSelectors.productDataWithChanges);
	const activeShopCurrency = useSelector(ShopSelectors.getShopCurrency);
	const isEditingDisabled = useSelector(ActiveProductSelectors.isEditingDisabled);
	const isAdmin = useSelector(UserSelectors.isAdmin);

	const { state, editState } = useDebouncedForm({
		memoizedInitialState: useMemo(
			() => ({
				pricing: product?.pricing ?? [],
				sales: product?.sales ?? null,
				rentals: product?.rentals ?? {
					enabled: false,
					basePrice: 0,
				},
				variants: product?.variants ?? {
					properties: [],
					options: [],
				},
				savedPricingTable: product?.savedPricingTable,
				useSavedPricingTable: product?.useSavedPricingTable,
				segmentPricings: product?.segmentPricings ?? [],
				terms: product?.terms,
				extensionPricing:
					product?.extensionPricing ?? getDefaultExtensionPricing(product.rentals?.basePrice ?? 0),
			}),
			[product],
		),
		memoizedOnDebounce: useCallback(
			(changes) => {
				dispatch(ActiveProductActions.editLocalProduct(changes));
			},
			[dispatch],
		),
	});

	const areBookingsEnabled = !!state?.rentals.enabled;

	const handleEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		editState({
			rentals: {
				...state.rentals,
				enabled: event.target.checked,
			},
			variants: {
				...state.variants,
				options: state.variants.options.map((variant) => {
					return {
						...variant,
						rentals: variant.rentals
							? {
									...variant.rentals,
									enabled: event.target.checked,
							  }
							: { enabled: event.target.checked, priceIncrease: null },
					};
				}),
			},
		});
	};

	const handleVariantsChange = (variants: ProductVariant[]) => {
		editState({
			variants: {
				...state.variants,
				options: variants,
			},
		});
	};

	const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
	const availableSegments = useMemo(() => {
		return SEGMENTS.filter((segment) => {
			return !state.segmentPricings.some(({ segments }) => segments.includes(segment));
		});
	}, [state.segmentPricings]);

	const renderSegmentSelect = (
		options: string[],
		selected: string[],
		onChange: (value: string[]) => void,
	) => {
		return (
			<Select
				variant="outlined"
				multiple
				value={selected}
				displayEmpty
				style={{ minWidth: '200px' }}
				renderValue={(selected: string[]) => {
					if (selected.length === 0) return t('pricing.selectSegments', 'Select segments');
					return selected.join(', ');
				}}
				onChange={(event: SelectChangeEvent<typeof selected>) => {
					onChange((event.target.value as unknown) as string[]);
				}}
				disabled={!!isEditingDisabled || !areBookingsEnabled}
			>
				{options.map((segment) => (
					<MenuItem key={segment} value={segment}>
						<Checkbox color="primary" checked={selected.indexOf(segment) !== -1} />
						<ListItemText primary={segment} />
					</MenuItem>
				))}
			</Select>
		);
	};

	return (
		<>
			<Box p={2}>
				<FormControlLabel
					control={
						<Switch
							checked={state.rentals?.enabled ?? false}
							onChange={handleEnabledChange}
							disabled={isEditingDisabled}
						/>
					}
					label={
						<Typography
							variant="h6"
							sx={{
								opacity: isEditingDisabled ? 0.3 : 1,
							}}
						>
							{t('product.bookings', 'Bookings')}
						</Typography>
					}
				/>
			</Box>
			{state.rentals?.enabled && !isAnyVariantForRent(state.variants.options) && (
				<Banner
					variant="warning"
					title={t('product.noVariantsCanBeBooked', 'No variants can be booked')}
					description={t(
						'product.noVariantsCanBeBookedDescription',
						'None of your variants have bookings enabled',
					)}
					button={{
						text: t('common:actions.fix', 'Fix'),
						onClick: () => {
							if (!!variantTableRef.current?.offsetTop) {
								window.scrollTo({ top: variantTableRef.current.offsetTop, behavior: 'smooth' });
							}
						},
					}}
				/>
			)}
			<ContainerBox>
				<Typography
					variant="h6"
					sx={{
						opacity: isEditingDisabled || !areBookingsEnabled ? 0.3 : 1,
					}}
				>
					{t('product.bookingPrice', 'Booking price')}
				</Typography>
				<PricingTable
					pricingTable={
						state.savedPricingTable && state.useSavedPricingTable
							? state.savedPricingTable
							: state.pricing ?? []
					}
					basePrice={state.rentals?.basePrice ?? 0}
					currency={activeShopCurrency}
					productId={product.id}
					onUseSavedTable={(tableName) => {
						editState({
							pricing: [],
							savedPricingTable: tableName,
							useSavedPricingTable: true,
						});
					}}
					onBasePriceChange={(basePrice) => {
						editState({
							rentals: {
								enabled: true,
								basePrice,
							},
						});
					}}
					onTableChange={(basePrice, items) => {
						editState({
							pricing: items,
							rentals: {
								enabled: true,
								basePrice,
							},
							savedPricingTable: undefined,
							useSavedPricingTable: false,
						});
					}}
					disabled={!!isEditingDisabled || !areBookingsEnabled}
					disableEditingTemplates={!isAdmin}
				/>
			</ContainerBox>
			<ContainerBox>
				<div ref={variantTableRef} />
				<VariantRentalPriceTable
					variants={state.variants.options}
					onVariantsChange={handleVariantsChange}
					productId={product.id}
					currency={activeShopCurrency}
					disabled={isEditingDisabled || !areBookingsEnabled}
				/>
			</ContainerBox>
			{state?.segmentPricings?.map((item, index) => (
				<ContainerBox key={`segments_${index}`}>
					<Box display="flex" flexDirection="row" alignItems="center" flexWrap="wrap">
						<Box mb={2} mr={2}>
							<Typography align="left" variant="h6">
								{t('pricing.segments', 'Segments')}:
							</Typography>
						</Box>
						<Box mb={2} mr={2}>
							{renderSegmentSelect(
								availableSegments.concat(item.segments),
								item.segments,
								(segments) => {
									if (segments.length === 0) {
										editState({
											segmentPricings: state.segmentPricings.filter((_, idx) => index !== idx),
										});
									} else {
										editState({
											segmentPricings: state.segmentPricings.map((item, idx) =>
												index === idx
													? {
															...item,
															segments,
													  }
													: item,
											),
										});
									}
								},
							)}
						</Box>
						<Box mb={2}>
							<IconButton
								onClick={() =>
									editState({
										segmentPricings: state.segmentPricings.filter((_, idx) => index !== idx),
									})
								}
								disabled={!!isEditingDisabled || areBookingsEnabled}
								size="large"
							>
								<DeleteIcon />
							</IconButton>
						</Box>
					</Box>
					<Typography variant="body2">
						{t(
							'pricing.segmentPricingDescription',
							'These pricing options are shown in addition to the default pricing options. If the row exists in both, the segment-specific row is shown in place of the default row.',
						)}
					</Typography>
					<PricingTable
						pricingTable={
							item.savedPricingTable && item.useSavedPricingTable
								? item.savedPricingTable
								: item.pricingTable ?? []
						}
						basePrice={item.fixedPrice || 0}
						currency={activeShopCurrency}
						disableFixedPrice={state.pricing.length > 0 || state.useSavedPricingTable}
						onUseSavedTable={(tableName) => {
							editState({
								segmentPricings: state.segmentPricings.map((item, idx) =>
									index === idx
										? {
												...item,
												pricingTable: [],
												savedPricingTable: tableName,
												useSavedPricingTable: true,
										  }
										: item,
								),
							});
						}}
						onBasePriceChange={(basePrice) => {
							editState({
								segmentPricings: state.segmentPricings.map((item, idx) =>
									index === idx
										? {
												...item,
												fixedPrice: basePrice,
										  }
										: item,
								),
							});
						}}
						onTableChange={(basePrice, items) => {
							editState({
								segmentPricings: state.segmentPricings.map((item, idx) =>
									index === idx
										? {
												pricingTable: items,
												fixedPrice: basePrice,
												useSavedPricingTable: false,
												segments: item.segments,
										  }
										: item,
								),
							});
						}}
						disabled={!!isEditingDisabled || !areBookingsEnabled}
						disableEditingTemplates={!isAdmin}
					/>
				</ContainerBox>
			))}
			{!isEditingDisabled && areBookingsEnabled && (
				<>
					<HasFeature
						requiredFeature="BETA_EXTENSIONS"
						fallback={(failedChecks) => {
							return !failedChecks.user ? <AddOnPromoBlock addOn="EXTENSIONS" /> : null;
						}}
					>
						<ContainerBox>
							<Typography variant="h6" gutterBottom>
								{t('product.extensionPricing.title', 'Extensions')}
							</Typography>
							<Typography variant="body1" gutterBottom>
								{t(
									'product.extensionPricing.description',
									'Allow your customers to extend their rentals on the fly, at a fixed rate per additional hour or day',
								)}
							</Typography>
							<ExtensionPricing
								value={state.extensionPricing}
								onChange={(value) => editState({ extensionPricing: value })}
								disabled={!!isEditingDisabled || !areBookingsEnabled}
							/>
						</ContainerBox>
					</HasFeature>
					<HasFeature requiredFeature="SEGMENTS">
						{availableSegments.length > 0 && (
							<ContainerBox>
								<Typography variant="h6" gutterBottom>
									{t('pricing.addSegmentPricing', 'Add segment-specific override')}
								</Typography>
								<Box display="flex" flexDirection="row" alignItems="center" flexWrap="wrap">
									<Box mt={2} mr={2}>
										{renderSegmentSelect(availableSegments, selectedSegments, setSelectedSegments)}
									</Box>
									<Box mt={2} mr={2}>
										<Button
											disabled={
												isEditingDisabled || !areBookingsEnabled || selectedSegments.length === 0
											}
											color="primary"
											variant="outlined"
											onClick={() => {
												editState({
													segmentPricings: state.segmentPricings.concat({
														useSavedPricingTable: false,
														pricingTable: [],
														fixedPrice: state.rentals?.basePrice ?? 0,
														segments: selectedSegments,
													}),
												});
												setSelectedSegments([]);
											}}
										>
											{t('common:actions.add')}
										</Button>
									</Box>
								</Box>
							</ContainerBox>
						)}
					</HasFeature>
				</>
			)}
		</>
	);
};

export default BookingPricing;
